import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef
} from "react";
import type { ChangeEvent, ForwardedRef, ReactNode } from "react";
import { cx } from "../../../utils";
import { InputSize } from "../Input";

const inputSize = {
  [InputSize.BASE]: "min-h-12",
  [InputSize.SM]: "min-h-10"
};

const translateOptional = (locale: string) => {
  switch (locale) {
    case "es":
      return " (opcional)";
    case "en":
    default:
      return " (optional)";
  }
};

interface TextAreaProps {
  label?: string | ReactNode;
  id: string;
  size?: InputSize;
  placeholder?: string;
  value?: string | number;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  autoFocus?: boolean;
  className?: string;
  error?: string | boolean;
  maxLength?: number;
  max?: number;
  checked?: boolean;
  disabled?: boolean;
  defaultValue?: string;
  borderLess?: boolean;
  rows?: number;
  required?: boolean;
  hideOptional?: boolean;
  locale?: string;
  cols?: number;
  onFocus?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  autoExpand?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;

  /**
   * Legacy prop for margin bottom.
   * Why?
   * When developing this input I accidentally added margin bottom input instead of mt to error message.
   * This created wrong visual sizing of the input.
   */
  legacyErrorMargins?: boolean;
}

const TextAreaComponent = (
  props: TextAreaProps,
  ref?: ForwardedRef<HTMLTextAreaElement>
) => {
  const {
    label,
    id,
    placeholder,
    onChange,
    size = InputSize.BASE,
    value,
    className,
    error,
    borderLess,
    onFocus: _onFocus,
    autoExpand = false,
    required,
    hideOptional,
    locale = "en",
    legacyErrorMargins: _legacyErrorMargins,
    ...rest
  } = props;

  const legacyErrorMargins = _legacyErrorMargins ?? true;

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useImperativeHandle(
    ref,
    () => textareaRef.current || ({} as HTMLTextAreaElement)
  );

  useEffect(() => {
    if (autoExpand && textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = "auto"; // Reset height
      textarea.style.height = textarea.value
        ? `${textarea.scrollHeight}px`
        : inputSize[size]; // Fallback to minHeight for empty value
    }
  }, [autoExpand, size, value]);

  const onFocus = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      e.currentTarget.scrollIntoView({ behavior: "smooth", block: "center" });
      if (_onFocus) {
        _onFocus(e);
      }
    },
    [_onFocus]
  );

  return (
    <>
      {!borderLess && label ? (
        <label
          className="text-content-700 mb-1 block text-sm font-medium"
          htmlFor={id}
        >
          {label}
          {!required && !hideOptional ? (
            <span className="text-content-100">
              {translateOptional(locale)}
            </span>
          ) : null}
        </label>
      ) : null}

      <textarea
        ref={textareaRef}
        className={cx(
          "placeholder:text-content-100 border-alpha-100 focus:border-primary-100 shadow-xs focus:shadow-focus-ring placeholder:text-content-100 hover border font-medium placeholder:text-base placeholder:font-normal focus:outline-0 focus:ring-0",
          "border-borders-500 w-full rounded-md border",
          legacyErrorMargins ? "mb-2" : "",
          inputSize[size],
          error ? "text-danger-700 border-danger-300" : "",
          borderLess
            ? "placeholder:text-content-100 border-0 border-transparent p-0 text-2xl text-gray-600 shadow-none placeholder:text-2xl placeholder:font-semibold placeholder:italic"
            : "p-2 px-3",
          autoExpand ? "resize-none overflow-hidden" : "",
          className
        )}
        id={id}
        name={id}
        onChange={(e) => {
          if (autoExpand) {
            e.target.style.height = "auto";
            e.target.style.height = `${e.target.scrollHeight}px`;
          }
          if (onChange) onChange(e);
        }}
        onFocus={onFocus}
        placeholder={placeholder}
        value={value}
        aria-invalid={Boolean(error)}
        rows={autoExpand ? 1 : undefined}
        {...rest}
      />
    </>
  );
};

const TextArea = forwardRef(TextAreaComponent);

export { TextArea };
